import { useEffect, useRef, useCallback } from 'react';
import * as echarts from 'echarts/core';
import type { EChartsCoreOption, EChartsType } from 'echarts/core';
import { BarChart } from 'echarts/charts';
import type { BarSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppStore } from '../../stores/app-store';

// Register only the components we need (tree-shaking friendly)
echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer,
]);

// ── Types ─────────────────────────────────────────────────────────────────────
type ChartOption = EChartsCoreOption;

interface TooltipParam {
  dataIndex: number;
  [key: string]: unknown;
}

// ── CSS var reader ────────────────────────────────────────────────────────────
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

function resolveColor(name: string, fallback: string): string {
  const val = cssVar(name);
  return val || fallback;
}

// ── Theme palette (reads live CSS vars from :root / .dark / .sepia) ──────────
export interface ChartPalette {
  primary: string;
  accent: string;
  foreground: string;
  mutedFg: string;
  muted: string;
  border: string;
  card: string;
  popover: string;
  popoverFg: string;
}

export function buildPalette(): ChartPalette {
  return {
    primary: resolveColor('--primary', '#4F66A8'),
    accent: resolveColor('--accent', '#7E94DA'),
    foreground: resolveColor('--foreground', '#1B2236'),
    mutedFg: resolveColor('--muted-foreground', '#69739A'),
    muted: resolveColor('--muted', '#E3E8F4'),
    border: resolveColor('--border', 'rgba(27,34,54,0.1)'),
    card: resolveColor('--card', '#F8FAFE'),
    popover: resolveColor('--popover', '#F8FAFE'),
    popoverFg: resolveColor('--popover-foreground', '#1B2236'),
  };
}

// ── Base ECharts option (grid / axis / tooltip defaults) ─────────────────────
export function buildBaseOption(palette: ChartPalette): ChartOption {
  return {
    animation: true,
    animationDuration: 400,
    backgroundColor: 'transparent',
    grid: {
      left: 0,
      right: 0,
      top: 4,
      bottom: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        color: palette.mutedFg,
        fontFamily: 'Inter, ui-sans-serif, sans-serif',
        fontSize: 10,
        fontWeight: 'bold',
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: {
        show: true,
        lineStyle: {
          color: palette.border,
          type: 'dashed',
          width: 1,
        },
      },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: palette.popover,
      borderColor: palette.border,
      borderWidth: 1,
      textStyle: {
        color: palette.popoverFg,
        fontFamily: 'Inter, ui-sans-serif, sans-serif',
        fontSize: 11,
      },
      extraCssText: 'border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);',
    },
  };
}

// ── Props ─────────────────────────────────────────────────────────────────────
export interface EChartsChartProps {
  /** Full ECharts option object — merged on top of base defaults. */
  option: ChartOption;
  /** Extra className for the wrapper div. */
  className?: string;
  /** Tailwind height class. Defaults to "h-40". */
  heightClass?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────
export function EChartsChart({ option, className = '', heightClass = 'h-40' }: EChartsChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<EChartsType | null>(null);

  // Track theme changes via the store (same approach as use-theme.ts)
  const theme = useAppStore((state) => state.settings.theme);

  // Build & apply merged option
  const applyOption = useCallback(() => {
    if (!chartRef.current) return;
    const palette = buildPalette();
    const base = buildBaseOption(palette);
    // Deep merge: base first, caller's option overrides on top
    const merged = echarts.util.merge(
      echarts.util.clone(base) as Record<string, unknown>,
      option as Record<string, unknown>,
      true
    ) as ChartOption;
    chartRef.current.setOption(merged, { notMerge: true, lazyUpdate: false });
  }, [option]);

  // Init chart on mount
  useEffect(() => {
    if (!containerRef.current) return;

    chartRef.current = echarts.init(containerRef.current, undefined, {
      renderer: 'canvas',
    });

    applyOption();

    // ResizeObserver keeps chart in sync with container size changes
    const ro = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply whenever option OR theme changes
  useEffect(() => {
    applyOption();
  }, [applyOption, theme]);

  return (
    <div className={`w-full ${heightClass} ${className}`}>
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}

// ── Helper: build a bar series option for weekly word-count data ──────────────
export interface WeekDayData {
  date: string;
  dayName: string;
  wordsWritten: number;
  isToday: boolean;
}

export function buildWeeklyBarOption(
  days: WeekDayData[],
  palette: ChartPalette,
  goalWords = 500
): ChartOption {
  const maxWords = Math.max(...days.map((d) => d.wordsWritten), goalWords, 1);

  const seriesData: BarSeriesOption['data'] = days.map((d) => ({
    value: d.wordsWritten,
    itemStyle: {
      color:
        d.wordsWritten >= goalWords
          ? palette.primary
          : d.wordsWritten > 0
            ? palette.accent
            : palette.muted,
      borderRadius: [4, 4, 0, 0],
      opacity: d.isToday ? 1 : 0.82,
    },
    label: d.isToday
      ? {
          show: true,
          position: 'top' as const,
          color: palette.primary,
          fontFamily: 'Inter, ui-sans-serif, sans-serif',
          fontSize: 10,
          fontWeight: 'bold' as const,
          formatter: () => 'hoje',
        }
      : { show: false },
  }));

  return {
    xAxis: {
      data: days.map((d) => d.dayName),
    },
    yAxis: {
      max: Math.ceil(maxWords * 1.15),
    },
    series: [
      {
        type: 'bar',
        name: 'Words',
        data: seriesData,
        // showBackground renders a "track" behind each bar → visible even at 0
        showBackground: true,
        backgroundStyle: {
          color: palette.muted,
          borderRadius: [4, 4, 0, 0],
          opacity: 0.45,
        },
        barMaxWidth: 36,
      } as BarSeriesOption,
    ],
    tooltip: {
      formatter: (params: TooltipParam | TooltipParam[]) => {
        const p = Array.isArray(params) ? params[0] : params;
        const day = days[p.dataIndex];
        return `<span style="font-weight:600">${day.dayName}</span><br/>${day.wordsWritten.toLocaleString()} palavras`;
      },
    },
  };
}
